// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import SnippetFolderIcon from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import { keyframes } from "@mui/material";
import Alert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { styled, useTheme, type Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  ConfirmResponse,
  useConfirmDialog,
} from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TableAddActions from "_shared/_views/tables/table-add-actions";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import {
  FeeScheduleTableCellNoValue,
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
} from "material-react-table";
import { MouseEvent, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import {
  isFeeScheduleTemplateLoadIssue
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import { proposalCollectionItemRevertValue } from "work/proposals/negotiation/proposal-collection-item-revert";
import {
  proposalFeeScheduleRestrictedTeamMemberText,
  proposalFeeScheduleTeamMemberFallbackText,
  proposalFeeScheduleUnassignedText,
} from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import { useRemovedProposalFeeScheduleCategories } from "work/proposals/negotiation/proposal-fee-schedule-removed-categories";
import {
  fieldHasPendingChange,
  pendingChangeTableRowStyles,
} from "work/proposals/comments/_view/pending-change-field-highlight";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import { useWorkPaneNarrowerThan } from "work/_views/use-work-pane-size";
import RowLink from "_shared/_views/tables/row-link";
import { useMeasuredWidthOfRef } from "_shared/_views/use-measured-width";
import {
  feeScheduleColumnWidth,
  fitFeeScheduleColumns,
} from "work/fee-schedule-templates/_views/tables/fee-schedule-table-columns";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import FeeScheduleCategory from "../../../../_shared/fee-schedules/categories/fee-schedule-category";
// #endregion

// #region Styled Components
/**
 * The band that says which row the issue traverser just took you to, drawn inside the row's cells.
 *
 * It used to be a ring *around* the row — `box-shadow: 0 0 0 3px` on the `<tr>` — and a table whose
 * borders are separate only shows such a ring if the rows are held apart from each other, so the
 * table opened 3px of `border-spacing` for as long as any row was pulsing. That cost two things.
 * The gaps themselves showed the table's own background between cells that are meant to read as one
 * row, over whatever the row's background was saying. And seven gaps across six columns added 21px
 * to a `table-layout: fixed` table that `fitFeeScheduleColumns` had already spent to the pixel, so
 * the table overflowed its container and grew a horizontal scrollbar with nothing to scroll to.
 *
 * Inset, on the cells, it costs no width at all and cannot be covered: a cell paints above its row,
 * which is why the same ring moved onto the `<tr>` as an `outline` disappears entirely. Top and
 * bottom only — a ring on each cell would draw a line at every column boundary — which is also the
 * shape `pendingChangeTableRowStyles` already uses to mark a row in this table.
 */
const pulseBorder = keyframes`
  0%, 100% {
    box-shadow: inset 0 1px 0 rgba(21, 101, 192, 0.1), inset 0 -1px 0 rgba(21, 101, 192, 0.1);
  }
  50% {
    box-shadow: inset 0 3px 0 rgba(21, 101, 192, 0.33), inset 0 -3px 0 rgba(21, 101, 192, 0.33);
  }
`;
const TableContainer = styled('div')(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
}));
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const LockedCellContent = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  verticalAlign: "middle",
  width: "100%",
});
/** How a removed category's cells read: struck through, in the disabled tone, like a removed redline row. */
function removedCategoryCellStyles(theme: Theme) {
  return {
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
    textDecorationColor: theme.palette.text.primary,
    textDecorationThickness: "2px",
    "& .MuiTypography-root": {
      color: "inherit",
      textDecoration: "inherit",
    },
  };
}
// #endregion

/** What this schedule's add control calls its three ways of gaining a category. */
export const ADD_NEW_CATEGORY_LABEL = "Add New Category";
export const ADD_EXISTING_CATEGORY_LABEL = "Add Existing Category";
export const FROM_EXISTING_TEMPLATE_LABEL = "From Existing Template";

const columnHelper = createMRTColumnHelper<
  FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency
>();

// #region Props
type FeeScheduleTemplatesTableProps = {
  className?: string;
  selectedCategories:
  | Array<FeeScheduleCategory>
  | Array<FeeScheduleCategoryMaskedCurrency>;
  disableEditing?: boolean;
  disableCategorySelection?: boolean;
  hideFromTemplateButton?: boolean;
  hideAddButton?: boolean;
  hideExistingButton?: boolean;
  /**
   * Hides each category row's change-history control, and with it the rows the history alone can
   * supply: the categories the schedule no longer has, drawn struck through (#4951).
   */
  hideFieldHistory?: boolean;
  hideActionsColumn?: boolean;
  hideTeamMemberColumn?: boolean;
  negotiationId?: string;
  viewerScope?: string;
  alertBannerMessage?: string;
  alertBannerSeverity?: AlertColor;
  activeReviewField?: ProposalField;
  fieldLockTooltip?: string;
  pendingChangeFields?: ProposalField[];
  /**
   * Extra actions rendered alongside the built-in add-category buttons in the top toolbar, so a
   * caller's own action (e.g. the proposal fee schedule tab's "Create Template") lines up with them
   * instead of sitting in a separate row above the table.
   */
  toolbarActions?: ReactNode;
  /**
   * An editor to draw underneath one row, instead of over the page in a dialog (issue #5111).
   *
   * Opt-in, and off wherever it is not passed: a caller that still opens a dialog from
   * `onEditCategory` gets the table exactly as it was. The caller says which row is open through
   * `editingCategoryId` and closes it from its own editor, because the row is not the only thing
   * that can end the edit — saving, cancelling and removing the category all do.
   *
   * The expander column MRT would add for this is deliberately hidden: the Edit action in the row
   * is what opens the editor, and a second control that opened the same thing would be two answers
   * to one question. It would also have no width under this table's fixed layout.
   */
  renderCategoryEditor?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => ReactNode;
  /** The id of the category whose inline editor is open. @see renderCategoryEditor */
  editingCategoryId?: string;
  onCreateCategoryClicked?: () => void;
  onExistingCategoryClicked?: () => void;
  onExistingTemplateClicked?: () => void;
  onEditCategory?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryRemoved?: (name: FeeScheduleCategoryName) => void;
  /**
   * Called with the category reverted to a prior value (parsed from that value's history entry).
   * The caller applies it the same way it applies any other category edit (replacing the entry by id
   * in the selected-categories array and re-saving via its own builder setter), so the revert flows
   * through the same `queueProposalUpdate` path as a manual edit and lands as a
   * `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
   */
  onCategoryReverted?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
};
// #endregion

export default function FeeScheduleTemplatesTable(
  props: Readonly<FeeScheduleTemplatesTableProps>,
) {
  const {
    className,
    selectedCategories,
    disableEditing,
    disableCategorySelection,
    hideFromTemplateButton,
    hideAddButton,
    hideExistingButton,
    hideFieldHistory,
    hideActionsColumn,
    hideTeamMemberColumn,
    negotiationId,
    viewerScope,
    alertBannerMessage,
    activeReviewField,
    fieldLockTooltip,
    pendingChangeFields,
    toolbarActions,
    renderCategoryEditor,
    editingCategoryId,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onExistingTemplateClicked,
    onEditCategory,
    onCategoryRemoved,
    onCategoryReverted,
  } = props;
  const alertBannerSeverity = props.alertBannerSeverity ?? "warning";

  // #region State
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);

  // #region Hooks
  const theme = useTheme();
  const session = useSession();
  const confirm = useConfirmDialog();
  /**
   * How much room this table has, which on a proposal page is not how much room the window has: the
   * page's comments and category panels take a column out of the row the table sits in, so a 1280px
   * display with comments open left the table 608px while `down("lg")` still called it a large one and
   * kept the desktop toolbar (#4964). Off a proposal page there is no pane to measure and both of
   * these answer from the viewport, exactly as they did.
   */
  const tableIsCompact = useWorkPaneNarrowerThan(theme.breakpoints.values.md);
  /**
   * The width this table actually has, which is neither the window's nor the proposal pane's: on a
   * proposal tab the templates column takes a share of the pane before the table sees any of it. It
   * decides which columns there is room to draw — see `fee-schedule-table-columns`.
   */
  // Measured on MRT's own table container rather than on the wrapper this component renders, because
  // that container is the box the columns have to fit inside — see `muiTableContainerProps` below.
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const tableWidth = useMeasuredWidthOfRef(tableContainerRef);
  /** Columns the reader hid or restored themselves. Layered over the width's answer, never under it. */
  const [chosenColumnVisibility, setChosenColumnVisibility] = useState<Record<string, boolean>>({});
  // #region Removed categories
  // The categories the schedule no longer has, read from its own history and kept as struck-through,
  // read-only rows so each keeps the history of the category it was (#4951) — the whole-schedule history
  // control that used to be the only place they survived is gone. They are not in `selectedCategories`,
  // so nothing that edits the schedule (a row click, Remove, a revert) is offered on them.
  const showFieldHistory = Boolean(!hideFieldHistory && negotiationId && viewerScope);
  const presentCategoryIds = useMemo(
    () => selectedCategories.flatMap((category) => (category.id ? [category.id.value] : [])),
    [selectedCategories],
  );
  const removedCategories = useRemovedProposalFeeScheduleCategories({
    negotiationId: showFieldHistory ? negotiationId : undefined,
    viewerScope: showFieldHistory ? viewerScope : undefined,
    presentItemIds: presentCategoryIds,
  });
  const removedCategoryIds = useMemo(
    () => new Set(removedCategories.map((removed) => removed.category.id.value)),
    [removedCategories],
  );
  const isRemovedCategory = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined) =>
      Boolean(category?.id && removedCategoryIds.has(category.id.value)),
    [removedCategoryIds],
  );
  const rows = useMemo(
    () => [...selectedCategories, ...removedCategories.map((removed) => removed.category)],
    [removedCategories, selectedCategories],
  );
  // #endregion

  const teamMemberUserIds = useMemo(
    () => new Set(rows.flatMap((category) => category.teamMember?.userId.value ?? [])),
    [rows],
  );
  const teamMemberIndividualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberUserIds.size > 0 ? { userIds: teamMemberUserIds } : skipToken,
  );
  const teamMemberNames = useMemo(
    () => new Map(
      (Individual.fromJSONArray(teamMemberIndividualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    ),
    [teamMemberIndividualsQuery.data],
  );
  /**
   * How one category's Team Member cell reads: the person's name, or the table's own word for the
   * states that have no name to give.
   *
   * Named rather than written inline because the column needs it twice, at two different moments —
   * see the note on the column itself.
   */
  const teamMemberText = useCallback(
    (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string => {
      if (category.teamMemberIsMasked) return proposalFeeScheduleRestrictedTeamMemberText;
      if (!category.teamMember) return proposalFeeScheduleUnassignedText;
      return (
        teamMemberNames.get(category.teamMember.userId.value) ?? proposalFeeScheduleTeamMemberFallbackText
      );
    },
    [teamMemberNames],
  );

  // #region Functions
  const handleRemoveButtonClicked = useCallback(
    async (
      event: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      event.stopPropagation();

      if (!category) return;

      const response = await confirm({
        title: "Delete Fee Schedule Category?",
        message: `Delete the ${category.name} fee schedule category?`,
        okButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;

      onCategoryRemoved?.(category.name);
    },
    [confirm, onCategoryRemoved],
  );

  const handleCategoryRevert = useCallback(
    (entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a fee-schedule category), so
      // `entry.operation` is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedCategory = FeeScheduleCategory.fromJSON(revertValue as never);
      if (!revertedCategory) return;
      onCategoryReverted?.(revertedCategory);
    },
    [onCategoryReverted],
  );

  const beginEditCategory = useCallback(
    (
      category:
        | FeeScheduleCategory
        | FeeScheduleCategoryMaskedCurrency
        | undefined
        | null,
    ) => {
      if (!category || disableEditing || isRemovedCategory(category)) return;
      onEditCategory?.(category);
    },
    [disableEditing, isRemovedCategory, onEditCategory],
  );
  // #endregion

  /**
   * The category's name, and the row's own statement that it opens something.
   *
   * A row here opens the inline editor when pressed, and used to say so with nothing but
   * `cursor: pointer` — the signal #5122 is about, the one you have to already be hovering to
   * receive. `RowLink` is the application's answer to that: underlined at rest, focusable, and a
   * button rather than an anchor because the editor opens in place and there is no URL to point at.
   *
   * A row that cannot be opened is handed no `onClick` at all, and `RowLink` then renders exactly
   * the text it rendered before — a locked schedule, a removed category and a load issue must not
   * claim to be editable.
   */
  const NameCell = useCallback(({ row, table }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    if (table.getState().isLoading) return;
    if (isFeeScheduleTemplateLoadIssue(row.original)) {
      return (
        <Typography color="error">{`Error loading category: ${row.original.issue}`}</Typography>
      );
    }
    const value = row.original.name.value;
    if (!value) return <FeeScheduleTableCellNoValue />;
    const opensEditor = !disableCategorySelection && !disableEditing && !isRemovedCategory(row.original);
    return (
      <RowLink ariaLabel={value} onClick={opensEditor ? () => beginEditCategory(row.original) : undefined}>
        <FeeScheduleTableCellText emphasized>{value}</FeeScheduleTableCellText>
      </RowLink>
    );
  }, [beginEditCategory, disableCategorySelection, disableEditing, isRemovedCategory]);

  const FeeCell = useCallback(({ row, table }: {
    row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
    table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
    let feeValue;
    if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
      feeValue = "Masked";
    } else if (row.original.fee == null) {
      feeValue = (
        <FeeScheduleTableCellText color="warning">{`Deferred to ${session.context?.viewingAsVendor ? "client" : "vendor"
          }`}</FeeScheduleTableCellText>
      );
    } else {
      feeValue = new Intl.NumberFormat(navigator.language, {
        style: "currency",
        currency: row.original.fee?.rate?.currency.toString() ?? "USD",
      }).format(row.original.fee?.rate?.amount ?? 0);
    }
    return feeValue;
  }, [session.context?.viewingAsVendor]);

  // #region Columns
  const columns = useMemo(
    () => [
      ...(disableEditing && fieldLockTooltip
        ? [
          columnHelper.display({
            header: "",
            id: "locked",
            size: 40,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                verticalAlign: "middle",
              },
            },
            Cell: () => (
              <LockedCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </LockedCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(getCategoryNameValue, {
        header: "Name",
        id: "name",
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: NameCell
      }),
      columnHelper.accessor(getCategoryDescriptionValue, {
        header: "Description",
        id: "description",
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: DescriptionCell
      }),
      columnHelper.accessor(getCategoryFixedFeeValue, {
        header: "Fixed?",
        id: "fixedFee",
        size: 50,
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: FixedFeeCell
      }),
      columnHelper.accessor(
        (category) => getCategoryFeeValue(category, session.context?.viewingAsVendor),
        {
          header: "Fee",
          id: "fee",
          size: 50,
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: FeeCell
        }
      ),
      columnHelper.accessor(getCategoryUnitValue, {
        header: "Per",
        id: "unit",
        size: 50,
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: UnitCell
      }),
      ...(hideTeamMemberColumn
        ? []
        : [
            columnHelper.accessor(teamMemberText, {
              header: "Team Member",
              id: "teamMember",
              enableGlobalFilter: true,
              filterFn: "includesString",
              /**
               * Read from the category, never from `cell.getValue()`.
               *
               * TanStack keeps a row's accessor values on the row object and rebuilds the row model
               * only when `data` changes identity — `getCoreRowModel` memoizes on `table.options.data`
               * and on nothing else, so a new column definition does not disturb a value already
               * cached there. The Individuals lookup that names the assignee answers after the first
               * render, and a schedule nobody has edited since hands the table the same categories,
               * so `cell.getValue()` returned the stand-in this column was first drawn with for as
               * long as the page stayed open: every staffed rate in the product read "Team member"
               * rather than a name (#5228). Resolving here instead means the reader sees the name as
               * soon as it exists. The accessor above remains what search and sorting read.
               */
              Cell: ({ row }) => <FeeScheduleTableCellText>{teamMemberText(row.original)}</FeeScheduleTableCellText>,
            }),
          ]),
      columnHelper.accessor(getCategoryBillingCodeValue, {
        header: "Code",
        id: "billingCode",
        size: 50,
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: BillingCodeCell
      }),
    ],
    [FeeCell, NameCell, disableEditing, fieldLockTooltip, hideTeamMemberColumn, session.context?.viewingAsVendor, teamMemberText]
  );
  // #endregion

  // #region Table Option Functions
  const getRowId = useCallback(
    (row: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency, index: number) => `${row?.id?.value ?? "category"}-${index}`,
    [],
  );

  const muiTableProps = useCallback(() => ({
    sx: {
      // See `fee-schedule-table-columns`: under the default `auto` layout a column is sized to its
      // longest cell, so the `nowrap` truncation these cells ask for could never apply and the table
      // grew past its container instead.
      tableLayout: "fixed" as const,
      // Stated rather than left out: separate borders come with 2px of user-agent spacing, and the
      // fit above has no width to spare for it. Nothing needs the rows held apart any more — see
      // `pulseBorder`, which used to raise this to 3px while a row was pulsing.
      borderSpacing: 0,
    },
  }), []);

  const muiTableBodyRowProps = useCallback(({ row }: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const hasPendingChange = fieldHasPendingChange(
      pendingChangeFields,
      ProposalField.FeeScheduleCategory(row.original.id),
    );
    return disableCategorySelection || disableEditing || isRemovedCategory(row.original)
      ? {
        sx: (theme: Theme) => ({
          ...(hasPendingChange ? pendingChangeTableRowStyles(theme) : {}),
          // On the cells, not the row — see `pulseBorder`. Where a row is both pending and the
          // active field, the animation takes the cells' `box-shadow` over from
          // `pendingChangeTableRowStyles`; that row's tint and underline colour still say it is
          // pending, and the traverser having just landed on it is the more urgent of the two.
          "& > td": {
            animation: isActiveFeeScheduleCategoryField(activeReviewField, row.original.id)
              ? `${pulseBorder} 1.5s infinite`
              : "none",
          },
          "& tr.MuiTableRow-root": {
            cursor: "default !important",
          },
          cursor: "default",
        }),
      }
      : {
        onClick: () => beginEditCategory(row.original),
        sx: (theme: Theme) => ({
          ...(hasPendingChange ? pendingChangeTableRowStyles(theme) : {}),
          "& > td": {
            animation: isActiveFeeScheduleCategoryField(activeReviewField, row.original.id)
              ? `${pulseBorder} 1.5s infinite`
              : "none",
          },
          cursor: "pointer",
        }),
      };
  }, [disableCategorySelection, disableEditing, isRemovedCategory, beginEditCategory, activeReviewField, pendingChangeFields]);

  const renderEmptyRowsFallback = useMemo(
    () =>
      emptyRowsFallback<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>({
        subject: "fee schedule categories",
      }),
    [],
  );

  /**
   * The ways this schedule can gain a category, as one split button rather than a row of up to three
   * equal ones (#4955). The first is the press the button itself makes; the rest are the alternatives
   * behind its dropdown. Which of them are offered is the caller's, exactly as before — a schedule that
   * hides "Add New Category" and keeps "From Existing Template" simply has that one as its press.
   */
  const addActions = useMemo(() => {
    if (disableEditing) return [];
    return [
      ...(hideAddButton ? [] : [{ label: ADD_NEW_CATEGORY_LABEL, onClick: () => onCreateCategoryClicked?.() }]),
      ...(hideExistingButton
        ? []
        : [
          {
            label: ADD_EXISTING_CATEGORY_LABEL,
            icon: <SnippetFolderIcon fontSize="small" />,
            onClick: () => onExistingCategoryClicked?.(),
          },
        ]),
      ...(hideFromTemplateButton
        ? []
        : [
          {
            label: FROM_EXISTING_TEMPLATE_LABEL,
            icon: <FileCopyIcon fontSize="small" />,
            onClick: () => onExistingTemplateClicked?.(),
          },
        ]),
    ];
  }, [
    disableEditing,
    hideAddButton,
    hideExistingButton,
    hideFromTemplateButton,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onExistingTemplateClicked,
  ]);

  /**
   * The search first, the add control beside it, then the caller's own actions and the view buttons at
   * the far end — one wrapping row, so the add control sits to the RIGHT of the search wherever there
   * is room and drops underneath where there is not (#4955).
   *
   * One site rather than two. The toolbar used to be drawn at whichever of MRT's two slots suited the
   * measured pane width; the row wraps now, which answers the same question from the real width at
   * every width, so `toolbarIsNarrow` decides nothing here any more.
   */
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        {!disableEditing && <TableAddActions actions={addActions} />}
        {!disableEditing && toolbarActions}
        {/* No whole-schedule history here: a category's history lives on its row, and a removed
            category keeps a row of its own (#4951). The narrow arrangement had kept a copy of it that
            the wide one had already lost, which is the kind of thing one toolbar rather than two puts
            a stop to. */}
        <CollectionToolbarViewButtons>
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleFullScreenButton table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    [addActions, disableEditing, toolbarActions],
  );

  // No whole-schedule history here: a category's history lives on its row, and a removed category keeps
  // a row of its own (#4951). Reading the schedule's history beside the categories' was the confusion.
  //
  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the page would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  const renderRowActions = useCallback((props: {
    row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  }) => {
    const { row } = props;
    // A row nobody may write to — the viewer is locked out, or the category is one the schedule no
    // longer has — carries the category's own history and nothing else, the read-only popover rather
    // than the revert menu (the same split the fee-schedule redline and the Details tab make). Scoped to
    // the category: the schedule as a whole has no history control of its own any more (#4951).
    if (disableEditing || isRemovedCategory(row.original)) {
      if (!showFieldHistory) return null;
      return (
        <RowActionsContainer>
          <ProposalFieldHistoryButton
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            collectionItemId={row.original.id.value}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
          />
        </RowActionsContainer>
      );
    }
    return (
      <RowActionsContainer>
        <Tooltip title="Remove Category">
          <span>
            <ProposalDeleteActionButton
              aria-label="Remove Category"
              onClick={(event) => handleRemoveButtonClicked(event, row.original)}
            />
          </span>
        </Tooltip>
        {!hideFieldHistory && (
          <ProposalFieldTimelineDropdown
            field={ProposalField.FeeScheduleCategory(row.original.id)}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            onRevert={handleCategoryRevert}
          />
        )}
      </RowActionsContainer>
    );
  }, [
    disableEditing,
    handleRemoveButtonClicked,
    handleCategoryRevert,
    hideFieldHistory,
    isRemovedCategory,
    negotiationId,
    showFieldHistory,
    viewerScope,
  ]);

  // #region Table Options
  /**
   * One answer for whether the actions column exists, read both by the fit below — which has to
   * reserve width for it — and by `enableRowActions`, which decides whether it is drawn. A locked
   * viewer still gets the column when there is history to open from it (#4951), and under a fixed
   * layout a column that is drawn without a width in the fit has nowhere to come from.
   */
  const showActionsColumn = !hideActionsColumn && (!disableEditing || showFieldHistory);
  const columnFit = useMemo(() => {
    const presentColumnIds = [
      ...(disableEditing && fieldLockTooltip ? ["locked"] : []),
      "name",
      "description",
      "fixedFee",
      "fee",
      "unit",
      ...(hideTeamMemberColumn ? [] : ["teamMember"]),
      "billingCode",
      ...(showActionsColumn ? ["mrt-row-actions"] : []),
    ];
    return fitFeeScheduleColumns(tableWidth, presentColumnIds);
  }, [disableEditing, fieldLockTooltip, hideTeamMemberColumn, showActionsColumn, tableWidth]);

  const columnVisibility = useMemo(
    () => ({
      ...columnFit.hidden,
      // The row's Edit action opens the inline editor, so MRT's own expander column would be a
      // second control for the same thing — and the fit above has no width to give it.
      ...(renderCategoryEditor ? { "mrt-row-expand": false } : {}),
      ...chosenColumnVisibility,
    }),
    [columnFit.hidden, chosenColumnVisibility, renderCategoryEditor],
  );

  /**
   * Which row, if any, is showing its editor — keyed the way `getRowId` keys rows rather than by the
   * category's own id, because that is the key MRT's expansion state is written in.
   */
  const expandedRows = useMemo(() => {
    if (!renderCategoryEditor || !editingCategoryId) return {};
    const index = rows.findIndex((row) => row?.id?.value === editingCategoryId);
    if (index < 0) return {};
    // Hoisted rather than written inline as `{ [getRowId(...)]: true }`: a call expression as a
    // computed key is one of the patterns the React Compiler cannot lower, and a bailout costs the
    // whole component its memoization silently. See app/CLAUDE.md -> React Compiler.
    const expandedRowId = getRowId(rows[index], index);
    return { [expandedRowId]: true };
  }, [editingCategoryId, getRowId, renderCategoryEditor, rows]);

  /**
   * Records what the reader chose in the Show/Hide columns menu, over what the width decided.
   *
   * The updater has to be applied to the *effective* visibility rather than to the overrides alone:
   * the menu toggles a column by negating what it believes is showing, and handing it a map that
   * only holds the reader's previous choices would have it read every untouched column as "not
   * hidden" and turn the first click on an auto-hidden column into a no-op.
   */
  const handleColumnVisibilityChange = useCallback(
    (
      updaterOrValue:
        | Record<string, boolean>
        | ((previous: Record<string, boolean>) => Record<string, boolean>),
    ) => {
      setChosenColumnVisibility((chosen) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue({ ...columnFit.hidden, ...chosen })
          : updaterOrValue,
      );
    },
    [columnFit.hidden],
  );

  const tableOptions = useMemo<
    MRT_TableOptions<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
  >(
    () => ({
      columns,
      data: rows,
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        showGlobalFilter: true,
        columnPinning: { right: ["mrt-row-actions"] },
        density: tableIsCompact ? "compact" : "comfortable",
      },
      enableColumnPinning: true,
      enableColumnFilters: true,
      enableGlobalFilter: true,
      enableGlobalFilterRankedResults: false,
      globalFilterFn: "includesString",
      manualFiltering: false,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      manualSorting: true,
      // A locked viewer still gets the actions column when there is history to open from it: without
      // the whole-schedule control, a category row is the only place its history can be reached.
      enableRowActions: showActionsColumn,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setQuery,
      onSortingChange: setSorting,
      rowCount: rows.length,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      ...(renderCategoryEditor
        ? {
          // Only the open row builds an editor. MRT asks every row for its panel, and a form per
          // row would mount a fee category editor for each category in the schedule.
          renderDetailPanel: ({ row }: {
            row: MRT_Row<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
          }) => (expandedRows[row.id] ? renderCategoryEditor(row.original) : null),
          muiDetailPanelProps: { sx: { padding: 0 } },
          enableExpandAll: false,
        }
        : {}),
      state: {
        columnFilters,
        columnVisibility,
        globalFilter: query,
        sorting,
        ...(renderCategoryEditor ? { expanded: expandedRows } : {}),
      },
      muiTableProps,
      /**
       * A fixed layout, so the column widths below are the widths the columns get and the cells'
       * existing ellipsis rules finally have something to truncate against. `minWidth` is the floor
       * `hiddenFeeScheduleColumns` decided there was room for; the preferred width above it is what
       * the leftover space is shared out towards.
       */
      muiTableHeadCellProps: ({ column }: { column: { id: string } }) => ({
        sx: {
          minWidth: feeScheduleColumnWidth(column.id).minimum,
          width: columnFit.widths[column.id],
        },
      }),
      /**
       * Opts the actions column out of the shared shrink-to-content rule in `MRTNoMemo`. That rule
       * declares a 1% width so a browser's auto table layout gives the column its content width and
       * the slack to the data columns — but this table is `table-layout: fixed`, where a declared
       * width is the width and `min-width` is not consulted. The column collapsed to 8px with its
       * buttons hanging outside it. Here the width comes from the fit above, like every other column.
       */
      displayColumnDefOptions: {
        "mrt-row-actions": {
          muiTableHeadCellProps: {
            align: "center" as const,
            sx: {
              px: 0.5,
              minWidth: feeScheduleColumnWidth("mrt-row-actions").minimum,
              width: columnFit.widths["mrt-row-actions"],
            },
          },
          muiTableBodyCellProps: {
            align: "center" as const,
            sx: { px: 0.5, "& > *": { justifyContent: "center" } },
          },
        },
      },
      muiTableBodyRowProps,
      muiTableBodyCellProps: ({ cell, row }) => ({
        sx: (cellTheme) => ({
          ...feeScheduleTableCellStyles(cellTheme, { columnId: cell.column.id }),
          // A removed category reads as a deletion, the way a removed row of a redline does.
          ...(isRemovedCategory(row.original) && cell.column.id !== "mrt-row-actions" && removedCategoryCellStyles(cellTheme)),
        }),
      }),
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      positionGlobalFilter: "none",
      /**
       * This is the box the columns have to fit inside, so this is the box that gets measured.
       *
       * `fitFeeScheduleColumns` shares out a width in pixels, and it was being handed the width of
       * the wrapper one level out — which has `overflow: hidden` and so never carries a scrollbar.
       * Once this container grew a vertical scrollbar its content box lost 10px that the share-out
       * had already spent, and the table came out 9px wider than the room it had (the tenth pixel
       * going to `Math.floor`). A fee schedule that scrolled sideways by a sliver, with no column
       * far enough over the edge to explain why (#5121). On a scaled-up display every pane is
       * narrower, so the vertical scrollbar arrives at a row count that fits comfortably otherwise,
       * which is why the reports came from high scaling.
       *
       * `scrollbar-gutter: stable` is what keeps the measurement from chasing itself. Narrowing the
       * columns to fit retires the horizontal scrollbar, which returns 10px of *height*, which can
       * retire the vertical scrollbar too — widening this box and inviting the share-out to spend
       * the width back. Reserving the gutter whether or not a scrollbar is in it means this box has
       * one width, so the observer settles on the first pass.
       */
      muiTableContainerProps: {
        ref: tableContainerRef,
        sx: {
          flex: 1,
          minWidth: 0,
          position: "relative",
          scrollbarGutter: "stable",
        },
      },
      muiTopToolbarProps: {
        sx: {
          // A zero floor on the inner Box is what lets MRT's toolbar grid track clamp to the pane
          // rather than size itself to its content — without it the toolbar, which is
          // `overflow: hidden`, clips whatever runs past the right edge and the one wrapping row
          // inside it never gets the chance to wrap.
          "& > .MuiBox-root": {
            alignItems: "center",
            flexDirection: "row",
            marginLeft: theme.spacing(1),
            minWidth: 0,
            ...(tableIsCompact && {
              flexDirection: "column",
              gap: 0,
              marginLeft: 0,
              padding: 0,
            }),
          },
        },
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: "Search",
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setQuery("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
    }),
    [
      theme,
      columns,
      rows,
      getRowId,
      isRemovedCategory,
      showActionsColumn,
      columnFilters,
      query,
      sorting,
      columnFit,
      columnVisibility,
      handleColumnVisibilityChange,
      tableIsCompact,
      muiTableProps,
      muiTableBodyRowProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions,
      renderCategoryEditor,
      expandedRows,
      tableContainerRef
    ],
  );
  // #endregion

  // #region Render
  return (
    <TableContainer className={className}>
      {alertBannerMessage && (
        <Alert
          severity={alertBannerSeverity}
          sx={{
            mb: 2,
            width: "100%",
            whiteSpace: "normal",
            "& .MuiAlert-message": {
              width: "100%",
            },
          }}
        >
          {alertBannerMessage}
        </Alert>
      )}
      <MRTNoMemo<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
        options={tableOptions}
      />
    </TableContainer>
  );
  // #endregion
}

function getCategoryNameValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return `Error loading category: ${category.issue}`;
  return category.name?.value ?? "";
}

function getCategoryDescriptionValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  return category.description?.value ?? "";
}

function getCategoryFixedFeeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category?.fee?.unit ? "No" : "Yes";
}

function getCategoryFeeValue(
  category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  viewingAsVendor?: boolean,
): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  if (!category.fee) return `Deferred to ${viewingAsVendor ? "client" : "vendor"}`;

  return new Intl.NumberFormat(navigator.language, {
    style: "currency",
    currency: category.fee?.rate?.currency.toString() ?? "USD",
  }).format(category.fee?.rate?.amount ?? 0);
}

function getCategoryUnitValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category.fee?.unit ?? "";
}

function getCategoryBillingCodeValue(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): string {
  if (isFeeScheduleTemplateLoadIssue(category)) return "";
  if (category instanceof FeeScheduleCategoryMaskedCurrency) return "Masked";
  return category.billingCode?.value ?? "";
}

export function isActiveFeeScheduleCategoryField(
  activeReviewField: ProposalField | undefined,
  categoryId: Guid,
): boolean {
  return activeReviewField?.isEqualTo(ProposalField.FeeScheduleCategory(categoryId)) ?? false;
}

// #region Cells
const DescriptionCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  const value = row.original.description?.value;
  return value ? <FeeScheduleTableCellText>{value}</FeeScheduleTableCellText> : <FeeScheduleTableCellNoValue />;
};

const FixedFeeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  let fixedValue;
  if (row.original instanceof FeeScheduleCategoryMaskedCurrency) {
    fixedValue = "Masked";
  } else if (row.original?.fee?.unit) {
    fixedValue = "No";
  } else {
    fixedValue = "Yes";
  }
  return fixedValue;
};

const UnitCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  return row.original instanceof FeeScheduleCategoryMaskedCurrency
    ? "Masked"
    : (row.original.fee?.unit ?? <FeeScheduleTableCellNoValue />);
};

const BillingCodeCell = ({ row, table }: {
  row: { original: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency },
  table: MRT_TableInstance<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>
}) => {
  if (table.getState().isLoading || isFeeScheduleTemplateLoadIssue(row.original)) return;
  return row.original instanceof FeeScheduleCategoryMaskedCurrency
    ? "Masked"
    : (row.original.billingCode?.value ?? <FeeScheduleTableCellNoValue />);
};
// #endregion
